/*
 * <PictureImage> — drop-in <img> replacement that lets the backend
 * serve WebP via HTTP Accept negotiation.
 *
 *   <PictureImage src="/hero.jpg" alt="…" loading="lazy" />
 *
 * Static /public paths (e.g. `/vision/hero.jpg`) are rewritten to
 * hit `/api/img/vision/hero.jpg`. That endpoint reads the request's
 * `Accept` header and serves `hero.webp` when the client advertises
 * `image/webp`, `hero.jpg` otherwise. `Vary: Accept` keeps caches
 * correct.
 *
 * Absolute URLs, `/api/…` paths and data-URIs pass through unchanged.
 *
 * `ref` is forwarded to the inner <img>.
 */
import React from 'react';

const REWRITEABLE = /^\/[^/]/;   // starts with `/` but not `//`

function optimizedSrc(src) {
    if (!src || typeof src !== 'string') return src;
    if (src.startsWith('/api/')) return src;                     // backend proxy already
    if (!REWRITEABLE.test(src)) return src;                       // absolute / data / relative
    // Static /public path — route through the negotiation endpoint.
    return `/api/img${src}`;
}

const PictureImage = React.forwardRef(function PictureImage(
    { src, alt = '', className, style, ...rest },
    ref,
) {
    const effectiveSrc = optimizedSrc(src);
    return (
        <img
            ref={ref}
            src={effectiveSrc}
            alt={alt}
            className={className}
            style={style}
            {...rest}
        />
    );
});

export default PictureImage;
